import { plural, t } from "@lingui/core/macro";
import type {
  CommitmentEndOfTermOutcome,
  CommitmentRenewalBehavior,
  CommitmentTermShiftDirection,
  EarlyExitCharge,
  SubscriptionDurationUnit,
} from "@/server-types";

/** Terms are derived, never stored: 12 periods of 1 month reads "12 mesecev". */
export function planTermLabel(
  periods: number,
  durationValue: number,
  durationUnit: SubscriptionDurationUnit,
): string {
  const count = periods * durationValue;

  switch (durationUnit) {
    case "day":
      return plural(count, {
        one: "# dan",
        two: "# dneva",
        few: "# dnevi",
        other: "# dni",
      });
    case "week":
      return plural(count, {
        one: "# teden",
        two: "# tedna",
        few: "# tedni",
        other: "# tednov",
      });
    case "month":
      return plural(count, {
        one: "# mesec",
        two: "# meseca",
        few: "# meseci",
        other: "# mesecev",
      });
    case "year":
      return plural(count, {
        one: "# leto",
        two: "# leti",
        few: "# leta",
        other: "# let",
      });
  }
}

/** Terms arrive in period order: the current or next one, else the last that ran. */
export function displayedCommitment<T extends { periodsRemaining: number }>(
  // Absent on subscription payloads shaped before commitments shipped.
  commitments: T[] | undefined | null,
): T | undefined {
  if (!commitments) {
    return undefined;
  }
  return (
    commitments.find((commitment) => commitment.periodsRemaining > 0) ??
    commitments.at(-1)
  );
}

/** What the job and the end-of-term endpoint act on; the displayed term is the one the member is in. */
export function lastSignedCommitment<T extends { firstPeriodNumber: number }>(
  commitments: T[] | undefined | null,
): T | undefined {
  return (commitments ?? []).reduce<T | undefined>(
    (latest, commitment) =>
      latest == null || commitment.firstPeriodNumber > latest.firstPeriodNumber
        ? commitment
        : latest,
    undefined,
  );
}

/**
 * A recorded charge is the only thing that says the member left: counting served
 * periods instead would flip to "potekla" on its own when the last one ends.
 */
export function commitmentRemainingLabel(term: {
  periodsRemaining: number;
  exitChargeCents: number | null;
}): string {
  if (term.periodsRemaining <= 0) {
    return term.exitChargeCents !== null
      ? t`Vezava je bila predčasno prekinjena`
      : t`Vezava je potekla`;
  }
  return plural(term.periodsRemaining, {
    one: "Še # obdobje do konca vezave",
    two: "Še # obdobji do konca vezave",
    few: "Še # obdobja do konca vezave",
    other: "Še # obdobij do konca vezave",
  });
}

/** Nothing left to run, so the term is history: a line in a list, not a card. */
export function isPastCommitment(term: { periodsRemaining: number }): boolean {
  return term.periodsRemaining <= 0;
}

/**
 * What still has to happen to a broken term's exit charge. `none` covers a term that ran
 * its course, and a charge of 0 is still a charge — the rule produced it deliberately.
 */
export type ExitChargeState =
  | "none"
  | "waived"
  | "outstanding"
  | "issuedEstimate"
  | "issuedInvoice";

export function exitChargeStateOf(term: {
  exitChargeCents: number | null;
  exitChargeWaived: boolean;
  exitChargeDocumentId: string | null;
  exitChargeDocumentType: "estimate" | "invoice" | null;
}): ExitChargeState {
  if (term.exitChargeCents === null) {
    return "none";
  }
  if (term.exitChargeWaived) {
    return "waived";
  }
  if (term.exitChargeDocumentId === null) {
    return "outstanding";
  }
  return term.exitChargeDocumentType === "invoice"
    ? "issuedInvoice"
    : "issuedEstimate";
}

/** Reads after the amount; `null` while nothing has happened to the charge yet. */
export function exitChargeStateLabel(state: ExitChargeState): string | null {
  switch (state) {
    case "waived":
      return t`odpisana`;
    case "issuedEstimate":
      return t`izdan predračun`;
    case "issuedInvoice":
      return t`izdan račun`;
    case "none":
    case "outstanding":
      return null;
  }
}

/** A charge with a document behind it, so there is something to open. */
export function hasIssuedExitChargeDocument(state: ExitChargeState): boolean {
  return state === "issuedEstimate" || state === "issuedInvoice";
}

/** The terse form for the history list, where the full sentence would wrap. */
export function pastCommitmentStatusLabel(term: {
  exitChargeCents: number | null;
}): string {
  return term.exitChargeCents !== null ? t`Predčasno prekinjena` : t`Potekla`;
}

/** Reads the signed rule back to the salon, so the charge is never a bare number. */
export function earlyExitChargeReason(estimate: {
  earlyExitCharge: EarlyExitCharge;
  chargeCents: number;
  minimumPaidPeriods: number;
  periods: number;
  periodsElapsed: number;
}): string {
  // A met floor or a term with nothing left owes nothing, and the rule's own sentence
  // would then read "še 0 obdobij". `none` keeps its wording: it says why, not just what.
  if (estimate.chargeCents === 0 && estimate.earlyExitCharge !== "none") {
    return t`Po dogovorjenem pravilu za to prekinitev ni stroška.`;
  }

  switch (estimate.earlyExitCharge) {
    case "none":
      return t`Ob sklenitvi vezave je bilo dogovorjeno, da predčasna prekinitev ni zaračunana.`;
    case "flatFee":
      return t`Ob sklenitvi vezave je bil dogovorjen fiksen znesek za predčasno prekinitev.`;
    case "savingClawback":
      // Billed, not used: the generation cron runs a period ahead, so the newest one is
      // on the member's account before they have set foot in it.
      return plural(estimate.periodsElapsed, {
        one: "Razlika med ceno brez vezave in ceno z vezavo za # obdobje, ki je stranki že zaračunano.",
        two: "Razlika med ceno brez vezave in ceno z vezavo za # obdobji, ki sta stranki že zaračunani.",
        few: "Razlika med ceno brez vezave in ceno z vezavo za # obdobja, ki so stranki že zaračunana.",
        other:
          "Razlika med ceno brez vezave in ceno z vezavo za # obdobij, ki so stranki že zaračunana.",
      });
    case "remainingPeriods":
      // The period being cancelled in is billed as usual, so only the ones after it count.
      return plural(estimate.periods - estimate.periodsElapsed, {
        one: "# preostalo obdobje vezave po ceni z vezavo.",
        two: "# preostali obdobji vezave po ceni z vezavo.",
        few: "# preostala obdobja vezave po ceni z vezavo.",
        other: "# preostalih obdobij vezave po ceni z vezavo.",
      });
    case "minimumPeriodsFloor": {
      const missing = Math.max(
        0,
        estimate.minimumPaidPeriods - estimate.periodsElapsed,
      );
      // Built outside the plural: a numeric key there reads as the exact-zero form.
      const shortfall = plural(missing, {
        one: "# obdobje",
        two: "# obdobji",
        few: "# obdobja",
        other: "# obdobij",
      });
      // The whole clause is pluralised: Slovenian agrees the verb and the participle
      // with the count, so the number cannot be interpolated into one fixed sentence.
      const agreed = plural(estimate.minimumPaidPeriods, {
        one: "Dogovorjeno je bilo najmanj # obdobje",
        two: "Dogovorjeni sta bili najmanj # obdobji",
        few: "Dogovorjena so bila najmanj # obdobja",
        other: "Dogovorjenih je bilo najmanj # obdobij",
      });
      return t`${agreed}, zato manjka še ${shortfall}.`;
    }
  }
}

/** What the term does when it runs out, so the card says it before the job does it. */
export function commitmentEndOfTermLabel(commitment: {
  renewalBehavior: CommitmentRenewalBehavior;
  noticeGivenAt: string | Date | null;
  endOfTermOutcome: CommitmentEndOfTermOutcome | null;
  periodsRemaining: number;
}): string | null {
  if (commitment.periodsRemaining <= 0) {
    return null;
  }
  // Decided during the term's last period, so for that period the card would otherwise
  // keep promising what the job has already done.
  switch (commitment.endOfTermOutcome) {
    case "expired":
      return t`Vezava se zaključi ob izteku tega obdobja`;
    case "rolledToRolling":
      return t`Vezava je potekla — paket teče naprej brez vezave`;
    case "renewed":
      return t`Vezava je bila samodejno podaljšana`;
    case null:
      break;
  }
  if (commitment.noticeGivenAt != null) {
    return t`Odpovedano — paket se zaključi ob izteku vezave`;
  }
  switch (commitment.renewalBehavior) {
    case "expire":
      return t`Ob izteku se paket zaključi`;
    case "rollToRolling":
      return t`Ob izteku teče naprej brez vezave`;
    case "renewSameTerm":
      return t`Ob izteku se vezava samodejno podaljša`;
    default:
      commitment.renewalBehavior satisfies never;
      return null;
  }
}

/** A notice expires the term whatever `renewalBehavior` says; mirrors the server's `decideEndOfTerm`. */
export function effectiveRenewalBehavior(
  renewalBehavior: CommitmentRenewalBehavior,
  hasNotice: boolean,
): CommitmentRenewalBehavior {
  return hasNotice ? "expire" : renewalBehavior;
}

/** True in the old term's last period, when the sweep has already signed the renewal being decided. */
export function decidesLaterTerm<
  T extends { id: number; periodsRemaining: number },
>(commitments: T[], editable: T | null): boolean {
  if (editable == null) {
    return false;
  }
  const served = displayedCommitment(commitments);
  return served != null && served.id !== editable.id;
}

/** The term the end-of-term decision can still change on; mirrors the endpoint's guard. */
export function editableCommitment<
  T extends {
    firstPeriodNumber: number;
    endOfTermOutcome: CommitmentEndOfTermOutcome | null;
  },
>(commitments: T[], isTermRunning: boolean): T | null {
  const running = lastSignedCommitment(commitments);
  if (!isTermRunning || running == null || running.endOfTermOutcome != null) {
    return null;
  }
  return running;
}

/**
 * Where a pending validity edit leaves the term. The window is derived from the period chain
 * rather than stored, so moving the dates moves the term's end — say where to, or the operator
 * is agreeing to a change nobody named.
 */
export function commitmentTermShiftLabel(shift: {
  direction: CommitmentTermShiftDirection;
  projectedEndFormatted: string;
}): string | null {
  switch (shift.direction) {
    case "extends":
      return t`Vezava se bo podaljšala do ${shift.projectedEndFormatted}.`;
    case "shortens":
      return t`Vezava se bo skrajšala do ${shift.projectedEndFormatted}.`;
    case "same":
      return null;
  }
}
